import { Heading, Text } from "@chakra-ui/react"
import { useColorModeValue } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import classnames from "classnames"
import PropTypes from "prop-types"
import React from "react"
import styled from "styled-components"
import ThemeBackground from "../background/ThemeBackground"
import Button, { buttonPropType } from "../button/Button"
import SelectButton from "../button/SelectButton"
import SubmitButton from "../button/SubmitButton"
import AnimatedIcon from "../pathicon/AnimatedIcon"
import AnimatedIcons from "../pathicon/AnimatedIcons"
import Pathicon from "../pathicon/Pathicon"
import {
  PRIMARY_GRAY,
  PRIMARY_WHITE,
  TERTIARY_GRAY,
  TERTIARY_WHITE
} from "../utils/colors"
import BlankImage from "./BlankImage"

const isReactNode = (action) => React.isValidElement(action)

const StyledContainer = styled.div`
  position: relative;
  text-align: center;
  margin: 0px auto;
  max-width: 500px;
  padding: 40px 20px;

  a:not(.Button) {
    text-decoration: underline;
  }
`

const StyledContent = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  height: 100%;
  white-space: initial;

  > *:not(:last-child) {
    margin-bottom: 15px;
  }
`

const StyledIcon = styled(Pathicon)`
  font-size: 24px;
  color: ${(props) => (props.inverted ? PRIMARY_WHITE : PRIMARY_GRAY)};
`

const StyledActionContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  width: 100%;

  ${(p) =>
    p.stack
      ? `
    flex-direction: column;
    align-items: center;

    /* Uniforms the widths of all SubmitButtons */
    .SubmitStatusButton,
    .SubmitStatusButton .Button.primary {
      width: 100%;
    }
    
    > * + * {
      margin-top: 10px;
    }
  `
      : `
    flex-direction: row;
    align-items: center;
    
    > * + * {
      margin-left: 10px;
    }
  `}
`

const StyledSecondaryActionContainer = styled.span`
  display: flex;
  white-space: pre;
`

const BlankSlate = ({
  icon,
  icons = null,
  animateIcon,
  heading,
  body,
  meta,
  footer,
  children,
  image,
  illustration,
  stack = false,
  theme = false,
  inverted = false,
  className,
  primaryAction,
  primaryActions,
  secondaryAction,
  secondaryActions,
  preActionContent
}) => {
  // Reverse the inversion when in dark mode.
  inverted = useColorModeValue(!!inverted, !inverted)

  // Assuming BlankSlate will only ever receive a singular or plural
  // of either the primary or secondary options, but not both.
  const actions = {
    primary: [].concat(primaryAction, primaryActions).filter(Boolean),
    secondary: [].concat(secondaryAction, secondaryActions).filter(Boolean)
  }

  return (
    <StyledContainer className={classnames("BlankSlate", className)}>
      {theme && <ThemeBackground rounded />}
      <StyledContent>
        {illustration ? (
          illustration
        ) : image ? (
          <BlankImage image={image} inverted={inverted} />
        ) : icon ? (
          animateIcon ? (
            <AnimatedIcon
              icon={icon}
              iconSize={30}
              circleSize={50}
              inverted={inverted}
            />
          ) : (
            <StyledIcon icon={icon} inverted={inverted} />
          )
        ) : icons ? (
          <AnimatedIcons icons={icons} inverted={inverted} />
        ) : null}
        {heading && (
          <Heading
            as="h4"
            size="xl"
            m={0}
            color={inverted ? "white" : undefined}
            textAlign="center"
          >
            {heading}
          </Heading>
        )}
        {body && (
          <Text m={0} color={inverted ? "white" : undefined} textAlign="center">
            {body}
          </Text>
        )}
        {preActionContent}
        {!!actions.primary.length && (
          <StyledActionContainer stack={stack}>
            {actions.primary.map((primaryAction, index) => (
              <React.Fragment key={index}>
                {isReactNode(primaryAction) ? (
                  primaryAction
                ) : primaryAction.options ? (
                  <SelectButton {...primaryAction} />
                ) : (
                  <SubmitButton
                    size="large"
                    styleType="primary"
                    {...primaryAction}
                    target={primaryAction.href ? "_blank" : null}
                    inverted={inverted}
                  />
                )}
              </React.Fragment>
            ))}
            {!!actions.secondary.length &&
              actions.secondary.map((secondaryAction, index) =>
                isReactNode(secondaryAction) ? (
                  <React.Fragment key={index}>{secondaryAction}</React.Fragment>
                ) : (
                  <StyledSecondaryActionContainer key={index}>
                    {!!secondaryAction.pre && (
                      <React.Fragment>{secondaryAction.pre} </React.Fragment>
                    )}
                    <Button
                      styleType="text"
                      {...secondaryAction}
                      inverted={inverted}
                    />
                  </StyledSecondaryActionContainer>
                )
              )}
          </StyledActionContainer>
        )}
        {meta && (
          <Text
            as="span"
            fontSize="sm"
            color={inverted ? TERTIARY_WHITE : TERTIARY_GRAY}
            textAlign="center"
          >
            {meta}
          </Text>
        )}
        {((!!footer || !!children) && footer) || children}
      </StyledContent>
    </StyledContainer>
  )
}

BlankSlate.displayName = "BlankSlate"

const primaryActionPropType = PropTypes.shape({
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  styleType: PropTypes.string,
  href: PropTypes.string,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onSubmitFailure: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
  onSubmitProcessing: PropTypes.func,
  // for SelectButton
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.node
  ]),
  // for SelectButton
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
        PropTypes.node
      ]).isRequired,
      action: PropTypes.func.isRequired,
      selected: PropTypes.bool
    })
  )
})

BlankSlate.propTypes = {
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  meta: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  footer: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  preActionContent: PropTypes.node,
  icon: PropTypes.string,
  icons: PropTypes.array,
  stack: PropTypes.bool,
  theme: PropTypes.bool,
  inverted: PropTypes.bool,
  animateIcon: PropTypes.bool,
  illustration: PropTypes.node,
  primaryAction: PropTypes.oneOfType([primaryActionPropType, PropTypes.node]),
  primaryActions: PropTypes.arrayOf(
    PropTypes.oneOfType([primaryActionPropType, PropTypes.node])
  ),
  secondaryAction: PropTypes.oneOfType([buttonPropType, PropTypes.node]),
  secondaryActions: PropTypes.arrayOf(
    PropTypes.oneOfType([buttonPropType, PropTypes.node])
  )
}

export default BlankSlate
